import React, { useEffect, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { toast } from "sonner";
import { Play, Terminal, Loader2, AlertTriangle, Braces, Database, Sparkles, Zap } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const DEFAULT_CODE = `Hi %%FirstName%%! Great to see a %%LoyaltyTier%% member from %%Region%%.

%%[
  SET @points = Lookup("loyalty", "Points", "SubscriberKey", SubscriberKey)
]%%
You currently have %%=V(@points)=%% loyalty points.`;

export default function AmpscriptPlaygroundPage() {
  const { refreshUser } = useAuth();
  const [code, setCode] = useState(DEFAULT_CODE);
  const [snippets, setSnippets] = useState([]);
  const [sampleData, setSampleData] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("snippets");

  useEffect(() => {
    api.get("/ampscript/snippets").then((r) => setSnippets(r.data));
    api.get("/ampscript/sample-data").then((r) => setSampleData(r.data));
  }, []);

  const runScript = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/ampscript/execute", { script: code });
      setResult(data);
      if (data.xp_awarded > 0) {
        toast.success(`+${data.xp_awarded} XP earned for your first successful run!`);
        refreshUser();
      }
    } catch (e) {
      toast.error("Failed to execute AMPscript");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="ampscript-playground-page">
      <div className="flex items-center gap-3 mb-2">
        <Terminal className="w-6 h-6 text-teal-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">SFMC Track</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">AMPscript Playground</h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">
        Write real AMPscript against a sandboxed sample subscriber and Data Extensions. See live output, errors,
        and variable state — no real SFMC org required.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-panel rounded-2xl p-1 overflow-hidden border border-teal-400/10" data-testid="ampscript-editor">
            <CodeMirror value={code} height="320px" theme={dracula} extensions={[html()]} onChange={(val) => setCode(val)} />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={runScript}
              disabled={running}
              data-testid="ampscript-run-btn"
              className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-slate-950 text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60 transition-colors"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run Script
            </button>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Zap className="w-3.5 h-3.5 text-amber-400" /> First successful run earns bonus XP
            </span>
          </div>

          {result && (
            <div className="glass-panel rounded-2xl p-5 border border-white/5" data-testid="ampscript-result-panel">
              <p className="text-[11px] font-bold uppercase tracking-wider text-teal-400 mb-2">Rendered Output</p>
              <pre
                className="text-sm text-slate-200 whitespace-pre-wrap bg-black/30 rounded-lg p-4 min-h-[60px]"
                data-testid="ampscript-output"
              >
                {result.output || "(empty output)"}
              </pre>

              {result.errors?.length > 0 && (
                <div className="mt-4" data-testid="ampscript-error-panel">
                  <p className="flex items-center gap-2 text-sm font-semibold text-red-400 mb-2">
                    <AlertTriangle className="w-4 h-4" /> {result.errors.length} error(s)
                  </p>
                  <ul className="space-y-1.5">
                    {result.errors.map((e, i) => (
                      <li key={`${i}-${e}`} className="text-xs text-red-300/90 bg-red-400/5 border border-red-400/10 rounded-lg p-2.5">
                        {e}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Object.keys(result.variables || {}).length > 0 && (
                <div className="mt-4">
                  <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                    <Braces className="w-3.5 h-3.5" /> Variables
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(result.variables).map(([k, v]) => (
                      <span
                        key={k}
                        data-testid={`ampscript-variable-${k}`}
                        className="text-xs font-mono px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300"
                      >
                        @{k} = <span className="text-teal-300">{JSON.stringify(v)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="glass-panel rounded-2xl p-4">
          <Tabs value={sidebarTab} onValueChange={setSidebarTab}>
            <TabsList data-testid="ampscript-sidebar-tabs" className="w-full grid grid-cols-2">
              <TabsTrigger value="snippets" data-testid="ampscript-sidebar-tab-snippets">
                <Sparkles className="w-3.5 h-3.5" />
              </TabsTrigger>
              <TabsTrigger value="data" data-testid="ampscript-sidebar-tab-data">
                <Database className="w-3.5 h-3.5" />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="snippets" className="mt-3 space-y-2">
              {snippets.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setCode(s.code)}
                  data-testid={`ampscript-snippet-${s.id}`}
                  className="w-full text-left text-xs bg-white/[0.02] hover:bg-teal-400/5 border border-white/5 hover:border-teal-400/20 rounded-lg p-3 transition-colors"
                >
                  <p className="text-slate-200 font-semibold">{s.title}</p>
                  <p className="text-slate-400 mt-1">{s.description}</p>
                </button>
              ))}
            </TabsContent>

            <TabsContent value="data" className="mt-3">
              {sampleData && (
                <div className="space-y-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-teal-400 mb-2">Sample Subscriber</p>
                    <div className="text-xs font-mono bg-black/30 rounded-lg p-3 space-y-1">
                      {Object.entries(sampleData.subscriber).map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-2">
                          <span className="text-slate-500">{k}</span>
                          <span className="text-slate-200 truncate">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {Object.entries(sampleData.data_extensions).map(([de, rows]) => (
                    <div key={de}>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">DE: {de}</p>
                      <div className="text-xs font-mono bg-black/30 rounded-lg p-3 space-y-2 max-h-40 overflow-auto">
                        {rows.map((row, i) => (
                          <div key={i} className="border-b border-white/5 pb-1 last:border-0 last:pb-0">
                            {Object.entries(row).map(([k, v]) => (
                              <div key={k} className="flex justify-between gap-2">
                                <span className="text-slate-500">{k}</span>
                                <span className="text-slate-200 truncate">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
